import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "ratingPanel",
    "overlay",
    "contentArea",
    "sectionBtn",
    "completionPercent",
    "progressBar",
    "progressText",
    "ratingForm",
    "lastSaved",
    "saveExitButton",
  ];

  connect() {
    console.log("=== Rating Tool controller connected ===");
    console.log("=== AUTOSAVE IS DISABLED FOR DEBUGGING ===");

    // Flag to prevent recursive long press calls
    this.isApplyingToAll = false;
    this.currentCompletion = 0;

    // Ensure hidden value fields are synced with any pre-selected radio buttons.
    // This covers the case where scale_item_id was saved but value was not,
    // so re-submissions work without the user having to re-click every button.
    this.syncPreSelectedValues();

    this.calculateCompletion();
    // TEMPORARILY DISABLED FOR DEBUGGING text_values persistence
    // this.setupAutosave()
    this.convertTimesToLocal();
    this.setupChangeListeners();
    this.setupLeaveWarning();
  }

  setupChangeListeners() {
    // Listen for any change events in the form to recalculate completion
    if (this.hasRatingFormTarget) {
      this.ratingFormTarget.addEventListener("change", () => {
        console.log("Form change detected, recalculating completion");
        this.calculateCompletion();
      });
    }
  }

  syncPreSelectedValues() {
    // Find all checked radio buttons and ensure their corresponding hidden
    // value fields are populated. This handles the case where a previous save
    // stored scale_item_id but not the value — the hidden field would render
    // empty, and without re-clicking, the value would never be set by JS.
    const checkedRadios = this.element.querySelectorAll(
      '.rating-item input[type="radio"]:checked'
    );
    let syncCount = 0;

    checkedRadios.forEach((radio) => {
      const itemContainer = radio.closest(".rating-item");
      if (!itemContainer) return;

      const valueField = itemContainer.querySelector(
        '[data-rating-portal--rating-tool-target="ratingValue"][type="hidden"]'
      );
      if (!valueField) return;

      // Only fill if the hidden field is currently empty
      if (!valueField.value || valueField.value.trim() === "") {
        const numericalValue = radio.dataset.numericalValue;
        const textValue = radio.dataset.textValue;
        const newValue = numericalValue || textValue;

        if (newValue) {
          valueField.value = newValue;
          syncCount++;
          console.log(
            `Synced pre-selected value for item ${valueField.dataset.itemId}: ${newValue}`
          );
        }
      }
    });

    if (syncCount > 0) {
      console.log(
        `syncPreSelectedValues: Filled ${syncCount} empty value field(s) from pre-selected radio buttons`
      );
    }
  }

  convertTimesToLocal() {
    // Convert all timestamps to local time
    document.querySelectorAll("[data-timestamp]").forEach((element) => {
      const timestamp = element.dataset.timestamp;
      if (timestamp) {
        const date = new Date(timestamp);

        // Check if this is a full datetime (has date info) or just time
        const parent = element.closest(".rating-panel-header");

        if (parent) {
          // Full datetime for header (Started)
          const dateTimeString = date.toLocaleString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
          element.textContent = dateTimeString;
        } else {
          // Just time for "Last saved"
          const timeString = date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
          element.textContent = timeString;
        }
      }
    });
  }

  disconnect() {
    if (this.autosaveInterval) {
      clearInterval(this.autosaveInterval);
    }

    this.teardownLeaveWarning();
  }

  toggleRatingPanel() {
    console.log("toggleRatingPanel called");
    console.log("ratingPanelTarget:", this.ratingPanelTarget);
    console.log("overlayTarget:", this.overlayTarget);

    this.ratingPanelTarget.classList.toggle("open");
    this.overlayTarget.classList.toggle("active");

    // Prevent body scroll when panel is open
    if (this.ratingPanelTarget.classList.contains("open")) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }

  showSection(event) {
    const button = event.currentTarget;
    const sectionName = button.dataset.section;

    // Update active button
    this.sectionBtnTargets.forEach((btn) => btn.classList.remove("active"));
    button.classList.add("active");

    // Show the selected section
    const contentArea = this.contentAreaTarget;
    const allSections = contentArea.querySelectorAll(".content-section");

    allSections.forEach((section) => {
      if (section.dataset.section === sectionName) {
        section.style.display = "block";
      } else {
        section.style.display = "none";
      }
    });
  }

  itemChanged(event) {
    const input = event.target;

    // Update value hidden field if this is a radio button
    if (input.type === "radio") {
      const itemContainer = input.closest(".rating-item");
      if (itemContainer) {
        const valueField = itemContainer.querySelector(
          '[data-rating-portal--rating-tool-target="ratingValue"]'
        );
        if (valueField) {
          const numericalValue = input.dataset.numericalValue;
          const textValue = input.dataset.textValue;
          valueField.value = numericalValue || textValue;

          // Trigger change event on the hidden field so completion calculation picks it up
          valueField.dispatchEvent(new Event("change", { bubbles: true }));
        }

        // Remove active class from all buttons in this item
        const allLabels = itemContainer.querySelectorAll(
          ".btn-group-toggle label"
        );
        allLabels.forEach((label) => {
          label.classList.remove("active", "btn-primary");
          label.classList.add("btn-outline-primary");
        });

        // Add active class to the clicked button's label
        const clickedLabel = input.closest("label");
        if (clickedLabel) {
          clickedLabel.classList.remove("btn-outline-primary");
          clickedLabel.classList.add("active", "btn-primary");
        }

        // Remove ALL existing definitions (both server-rendered and JS-created)
        const existingDefinitions =
          itemContainer.querySelectorAll(".alert-info");
        existingDefinitions.forEach((def) => def.remove());

        // Show/update definition if available
        const description = input.dataset.description;

        if (description && description.trim() !== "") {
          // Create new definition display
          const definitionHtml = `
            <div class="alert alert-info mt-3 mb-0" style="background-color: #EFF6FF; border-color: #BFDBFE;">
              <small class="font-weight-bold text-primary">
                <i class="fas fa-info-circle mr-1"></i>Definition:
              </small>
              <br>
              <small class="definition-text">${description}</small>
            </div>
          `;
          const buttonsContainer =
            itemContainer.querySelector(".btn-group-toggle");
          if (buttonsContainer) {
            buttonsContainer.insertAdjacentHTML("afterend", definitionHtml);
          }
        }
      }
    }

    // Recalculate completion when an item value changes
    this.calculateCompletion();
  }

  handleLongPress(event) {
    event.preventDefault(); // Prevent default context menu
    event.stopPropagation(); // Stop event from bubbling

    // Prevent recursive calls
    if (this.isApplyingToAll) {
      console.log("Already applying to all, ignoring...");
      return;
    }

    const input = event.target;
    if (!input || input.type !== "radio") return;

    const scaleItemId = input.value;
    const ratingScaleId = input.dataset.ratingScaleId;
    const numericalValue = input.dataset.numericalValue;
    const textValue = input.dataset.textValue;
    const description = input.dataset.description;
    const pressedValue = numericalValue || textValue;

    if (
      !confirm(
        "Would you like to apply this rating to all items with the same rating scale in all sections?"
      )
    ) {
      return;
    }

    // Set flag to prevent recursive calls
    this.isApplyingToAll = true;

    console.log(
      `Applying scale item ${scaleItemId} (value: ${pressedValue}) to all items with rating scale ${ratingScaleId}`
    );

    // Find all rating items that have the same rating scale
    const form = this.ratingFormTarget;
    const allRatingItems = form.querySelectorAll(".rating-item");

    let appliedCount = 0;
    allRatingItems.forEach((item) => {
      // Check if this item has the same rating scale
      const itemRatingScaleId = item.dataset.ratingScaleId;
      if (itemRatingScaleId === ratingScaleId) {
        // Find the radio button with this scale item value
        const targetRadio = item.querySelector(
          `input[type="radio"][value="${scaleItemId}"]`
        );
        if (targetRadio) {
          // Click the radio button to select it
          targetRadio.checked = true;
          // Trigger the change event to update the UI and hidden field
          // But DON'T include contextmenu in this synthetic event
          const changeEvent = new Event("change", { bubbles: true });
          targetRadio.dispatchEvent(changeEvent);
          appliedCount++;
        }
      }
    });

    // Recalculate completion
    this.calculateCompletion();

    console.log(`Applied "${pressedValue}" to ${appliedCount} items`);

    // Reset flag after a short delay to allow all events to complete
    setTimeout(() => {
      this.isApplyingToAll = false;
    }, 100);
  }

  calculateCompletion() {
    if (!this.hasRatingFormTarget) return;

    const form = this.ratingFormTarget;

    // Get all mandatory inputs, but group text_values by item_id
    const mandatoryInputs = form.querySelectorAll('[data-mandatory="true"]');

    // Group inputs by item_id for text_values fields
    const itemGroups = {};
    const standaloneInputs = [];

    mandatoryInputs.forEach((input) => {
      const itemId = input.dataset.itemId;
      const name = input.name || "";

      // Check if this is a text_values field
      if (name.includes("[text_values][")) {
        if (!itemGroups[itemId]) {
          itemGroups[itemId] = [];
        }
        itemGroups[itemId].push(input);
      } else {
        standaloneInputs.push(input);
      }
    });

    let totalMandatory =
      standaloneInputs.length + Object.keys(itemGroups).length;
    let completed = 0;

    console.log(
      `Calculating completion: Found ${
        standaloneInputs.length
      } standalone mandatory inputs and ${
        Object.keys(itemGroups).length
      } text_values item groups`
    );

    // Check standalone inputs
    standaloneInputs.forEach((input) => {
      const hasValue = input.value && input.value.trim() !== "";
      if (hasValue) {
        completed++;
      }
      console.log(
        `Standalone Input ${input.name || input.dataset.itemId}: ${
          hasValue ? "FILLED" : "EMPTY"
        } (value: "${input.value}")`
      );
    });

    // Check text_values groups - item is complete if ANY field has a value
    Object.keys(itemGroups).forEach((itemId) => {
      const inputs = itemGroups[itemId];
      const hasAnyValue = inputs.some(
        (input) => input.value && input.value.trim() !== ""
      );
      if (hasAnyValue) {
        completed++;
      }
      console.log(
        `Text_values group for item ${itemId}: ${
          hasAnyValue ? "FILLED" : "EMPTY"
        } (${inputs.length} fields)`
      );
    });

    // Check for signature
    const signatureInput = form.querySelector(
      '[name*="candidate_signature_img"]'
    );
    if (signatureInput) {
      totalMandatory++;
      const hasSignature =
        signatureInput.value && signatureInput.value.trim() !== "";
      if (hasSignature) {
        completed++;
      }
      console.log(`Signature: ${hasSignature ? "SIGNED" : "NOT SIGNED"}`);
    }

    const percentage =
      totalMandatory > 0 ? Math.round((completed / totalMandatory) * 100) : 0;
    this.currentCompletion = percentage;

    console.log(`Completion: ${completed}/${totalMandatory} = ${percentage}%`);

    // Update progress indicators
    if (this.hasCompletionPercentTarget) {
      this.completionPercentTarget.textContent = percentage;
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`;
      this.progressBarTarget.setAttribute("aria-valuenow", percentage);

      // Change color based on completion
      this.progressBarTarget.classList.remove(
        "bg-danger",
        "bg-warning",
        "bg-success"
      );
      if (percentage < 50) {
        this.progressBarTarget.classList.add("bg-danger");
      } else if (percentage < 100) {
        this.progressBarTarget.classList.add("bg-warning");
      } else {
        this.progressBarTarget.classList.add("bg-success");
      }
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${percentage}% Complete`;
    }

    this.updateSaveAndExitState(percentage);
  }

  setupLeaveWarning() {
    this.beforeUnloadHandler = (event) => {
      if (!this.shouldWarnBeforeLeave()) return;

      event.preventDefault();
      // Browsers ignore custom text and show a standard warning message.
      event.returnValue = "";
      return "";
    };

    this.beforeVisitHandler = (event) => {
      if (!this.shouldWarnBeforeLeave()) return;

      const confirmed = window.confirm(
        "This rating is incomplete. Are you sure you want to leave this page?"
      );

      if (!confirmed) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", this.beforeUnloadHandler);
    document.addEventListener("turbolinks:before-visit", this.beforeVisitHandler);
  }

  teardownLeaveWarning() {
    if (this.beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    }

    if (this.beforeVisitHandler) {
      document.removeEventListener(
        "turbolinks:before-visit",
        this.beforeVisitHandler
      );
    }
  }

  shouldWarnBeforeLeave() {
    return Number(this.currentCompletion || 0) < 100;
  }

  updateSaveAndExitState(percentage) {
    if (!this.hasSaveExitButtonTarget) return;

    const complete = percentage === 100;
    this.saveExitButtonTarget.disabled = !complete;

    if (complete) {
      this.saveExitButtonTarget.removeAttribute("title");
    } else {
      this.saveExitButtonTarget.setAttribute(
        "title",
        "Complete all mandatory items before exiting."
      );
    }
  }

  setupAutosave() {
    // TEMPORARILY DISABLED FOR DEBUGGING text_values persistence issue
    console.log("⚠️  Autosave is DISABLED for debugging");
    return;

    // Autosave every 30 seconds (30000ms)
    console.log("Setting up autosave - will run every 30 seconds");
    this.autosaveInterval = setInterval(() => {
      console.log("Autosave interval triggered");
      this.autosave();
    }, 30000);

    // Run once after 5 seconds for testing
    setTimeout(() => {
      console.log("Initial autosave after 5 seconds");
      this.autosave();
    }, 5000);
  }

  async autosave() {
    if (!this.hasRatingFormTarget) {
      console.error("Autosave: No rating form target found");
      return;
    }

    const form = this.ratingFormTarget;
    const formData = new FormData(form);

    // Remove _method field that Rails adds for PATCH - autosave must use POST
    formData.delete("_method");

    // Construct autosave URL from form action
    // Form action is like: /events/5/sessions/abc-123/scenarios/193/ratings/xyz-456
    const url = `${form.action}/autosave`;

    console.log("Autosaving to:", url);
    console.log("Form action:", form.action);

    try {
      const response = await fetch(url, {
        method: "POST", // Must be POST, not PATCH
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
      });

      console.log("Autosave response status:", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log("Autosave response data:", data);

        if (this.hasLastSavedTarget && data.last_autosave_at) {
          const date = new Date(data.last_autosave_at);
          const timeString = date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
          console.log("Updating last saved time to:", timeString);
          this.lastSavedTarget.textContent = timeString;
          this.lastSavedTarget.dataset.timestamp = data.last_autosave_at;
        } else {
          console.error(
            "Last saved target not found or no timestamp in response"
          );
        }

        // Show brief success indicator
        this.showSaveIndicator("✓ Saved");
      } else {
        const errorText = await response.text();
        console.error(
          "Autosave failed:",
          response.status,
          response.statusText,
          errorText
        );
      }
    } catch (error) {
      console.error("Autosave error:", error);
    }
  }

  showSaveIndicator(message) {
    const indicator = document.createElement("div");
    indicator.className =
      "alert alert-success alert-dismissible fade show position-fixed";
    indicator.style.cssText = "top: 70px; right: 20px; z-index: 9999;";
    indicator.innerHTML = `
      <i class="fas fa-check-circle mr-2"></i>${message}
      <button type="button" class="close" data-dismiss="alert">
        <span>&times;</span>
      </button>
    `;

    document.body.appendChild(indicator);

    setTimeout(() => {
      indicator.remove();
    }, 3000);
  }

  openResource(event) {
    const button = event.currentTarget;
    const resourceType = button.dataset.resourceType;

    // Get the resource data URL based on type
    const eventId = this.element.dataset.eventId;
    const sessionId = this.element.dataset.sessionId;
    const scenarioId = this.element.dataset.scenarioId;

    let url = "";
    switch (resourceType) {
      case "labs":
        url = `/events/${eventId}/sessions/${sessionId}/scenarios/${scenarioId}/labs`;
        break;
      case "ecgs":
        url = `/events/${eventId}/sessions/${sessionId}/scenarios/${scenarioId}/ecgs`;
        break;
      case "xrays":
        url = `/events/${eventId}/sessions/${sessionId}/scenarios/${scenarioId}/xrays`;
        break;
      case "attachments":
        url = `/events/${eventId}/sessions/${sessionId}/scenarios/${scenarioId}/attachments`;
        break;
    }

    // Open in a modal or new window
    window.open(url, "_blank", "width=1200,height=800");
  }
}
